import { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";

export default function ShareLinks({ onClose }) {
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [label, setLabel] = useState("");
  const [days, setDays] = useState(30);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/requisition-invites");
      setInvites(r.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/requisition-invites", { label, expires_in_days: Number(days) });
      const url = r.data.share_url.startsWith("http") ? r.data.share_url : `${window.location.origin}${r.data.share_url}`;
      try {
        await navigator.clipboard.writeText(url);
        toast.success("Link created + copied to clipboard");
      } catch {
        toast.success("Link created");
      }
      setLabel("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (id) => {
    if (!window.confirm("Revoke this link? It will stop accepting submissions.")) return;
    try {
      await api.delete(`/requisition-invites/${id}`);
      toast.success("Link revoked");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const copy = (inv) => {
    const url = inv.share_url.startsWith("http") ? inv.share_url : `${window.location.origin}${inv.share_url}`;
    navigator.clipboard.writeText(url).then(
      () => toast.success("Copied"),
      () => toast.error("Copy failed"),
    );
  };

  return (
    <div className="fixed inset-0 bg-white/80 backdrop-blur-md flex items-center justify-center z-50 p-4 overflow-auto">
      <div className="bg-white border border-ink max-w-2xl w-full p-6 fade-in" data-testid="share-links-modal">
        <div className="flex items-start justify-between">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-quiet">Team access · Requisition requests</div>
            <h3 className="font-serif text-3xl text-ink mt-1 leading-tight">Share a requisition link.</h3>
            <p className="text-sm text-quiet mt-2 max-w-lg">
              Send the link to anyone in your org — they can fill a job request without a login.
              Submissions land in your requisition queue as <span className="font-mono">Pending HR Review</span>.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-quiet hover:text-ink text-xl leading-none"
            data-testid="close-share-links"
          >
            ×
          </button>
        </div>

        <form onSubmit={create} className="mt-5 flex flex-wrap gap-2 items-end border-b border-rule pb-5">
          <div className="flex-1 min-w-[200px]">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-quiet">Label (optional)</div>
            <input
              data-testid="invite-label"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Q2 engineering hiring"
              className="mt-1.5 w-full border border-rule px-3 py-2 text-sm focus:outline-none focus:border-klein"
            />
          </div>
          <div className="w-28">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-quiet">Expires (days)</div>
            <input
              data-testid="invite-days"
              type="number"
              min={1}
              max={365}
              value={days}
              onChange={(e) => setDays(e.target.value)}
              className="mt-1.5 w-full border border-rule px-3 py-2 text-sm focus:outline-none focus:border-klein"
            />
          </div>
          <button
            data-testid="create-invite-btn"
            type="submit"
            disabled={busy}
            className="bg-klein hover:bg-klein-hover text-white px-4 py-2 text-sm font-medium disabled:opacity-50"
          >
            {busy ? "Creating…" : "+ New link"}
          </button>
        </form>

        <div className="mt-4 max-h-[360px] overflow-auto">
          {loading && <div className="py-8 text-center text-quiet text-sm">Loading…</div>}
          {!loading && invites.length === 0 && (
            <div className="py-8 text-center text-quiet text-sm">No links yet — create one above.</div>
          )}
          {!loading && invites.length > 0 && (
            <ul className="divide-y divide-rule">
              {invites.map((inv) => {
                const expired = inv.expires_at && inv.expires_at < new Date().toISOString();
                const dead = !inv.active || expired;
                return (
                  <li key={inv.id} className="py-3" data-testid={`invite-row-${inv.id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-ink text-sm">{inv.label || "Untitled link"}</span>
                          {dead && (
                            <span className="px-2 py-0.5 border border-[#EF4444] text-[10px] font-mono uppercase tracking-wider text-[#EF4444]">
                              {!inv.active ? "Revoked" : "Expired"}
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-quiet font-mono mt-0.5 truncate">
                          {inv.share_url.startsWith("http") ? inv.share_url : `${window.location.origin}${inv.share_url}`}
                        </div>
                        <div className="text-[10px] text-quiet font-mono mt-1 flex gap-2 flex-wrap">
                          <span>By {inv.created_by?.email}</span>
                          <span>·</span>
                          <span>{inv.uses_count} submission{inv.uses_count === 1 ? "" : "s"}</span>
                          {inv.expires_at && (
                            <>
                              <span>·</span>
                              <span>Expires {new Date(inv.expires_at).toLocaleDateString()}</span>
                            </>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2 shrink-0">
                        {!dead && (
                          <button
                            data-testid={`copy-${inv.id}`}
                            onClick={() => copy(inv)}
                            className="border border-ink hover:bg-paper px-3 py-1.5 text-xs font-mono uppercase tracking-wider"
                          >
                            Copy
                          </button>
                        )}
                        {inv.active && (
                          <button
                            data-testid={`revoke-${inv.id}`}
                            onClick={() => revoke(inv.id)}
                            className="border border-[#EF4444] text-[#EF4444] hover:bg-[#FEF2F2] px-3 py-1.5 text-xs font-mono uppercase tracking-wider"
                          >
                            Revoke
                          </button>
                        )}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
